import React, { useState } from 'react';
import './LibraryReports.css';

const LibraryReports = ({ books, readers, borrowings, onBack }) => {
  const [selectedReport, setSelectedReport] = useState('circulation');
  const [dateRange, setDateRange] = useState('month'); // 'week', 'month', 'quarter', 'year'
  const [exportFormat, setExportFormat] = useState('pdf'); // 'pdf', 'excel', 'csv'

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('zh-HK');
  };

  const calculateDateRange = (range) => {
    const today = new Date();
    const startDate = new Date();
    
    switch (range) {
      case 'week':
        startDate.setDate(today.getDate() - 7);
        break;
      case 'month':
        startDate.setMonth(today.getMonth() - 1);
        break;
      case 'quarter':
        startDate.setMonth(today.getMonth() - 3);
        break;
      case 'year':
        startDate.setFullYear(today.getFullYear() - 1);
        break;
      default:
        startDate.setMonth(today.getMonth() - 1);
    }
    
    return { startDate, endDate: today };
  };

  const getDateRangeLabel = (range) => {
    switch (range) {
      case 'week':
        return '最近一週';
      case 'month':
        return '最近一個月';
      case 'quarter':
        return '最近一季';
      case 'year':
        return '最近一年';
      default:
        return '最近一個月';
    }
  };

  const generateCirculationReport = () => {
    const { startDate, endDate } = calculateDateRange(dateRange);
    
    const periodBorrowings = borrowings.filter(borrowing => {
      const borrowDate = new Date(borrowing.borrowDate);
      return borrowDate >= startDate && borrowDate <= endDate;
    });

    const totalBorrowings = periodBorrowings.length;
    const totalReturns = periodBorrowings.filter(b => b.status === 'returned').length;
    const totalRenewals = periodBorrowings.filter(b => b.renewed).length;
    const totalOverdue = periodBorrowings.filter(b => {
      const dueDate = new Date(b.dueDate);
      return dueDate < new Date() && b.status === 'borrowed';
    }).length;

    const topBooks = books
      .map(book => {
        const bookBorrowings = periodBorrowings.filter(b => b.bookId === book.id);
        return {
          ...book,
          borrowCount: bookBorrowings.length
        };
      })
      .filter(book => book.borrowCount > 0)
      .sort((a, b) => b.borrowCount - a.borrowCount)
      .slice(0, 10);

    const topReaders = readers
      .map(reader => {
        const readerBorrowings = periodBorrowings.filter(b => b.readerId === reader.id);
        return {
          ...reader,
          borrowCount: readerBorrowings.length
        };
      })
      .filter(reader => reader.borrowCount > 0)
      .sort((a, b) => b.borrowCount - a.borrowCount)
      .slice(0, 10);

    return {
      period: getDateRangeLabel(dateRange),
      totalBorrowings,
      totalReturns,
      totalRenewals,
      totalOverdue,
      topBooks,
      topReaders
    };
  };

  const generateOverdueReport = () => {
    const overdueBorrowings = borrowings.filter(borrowing => {
      const dueDate = new Date(borrowing.dueDate);
      const today = new Date();
      return dueDate < today && borrowing.status === 'borrowed';
    });

    const overdueByReader = readers.map(reader => {
      const readerOverdue = overdueBorrowings.filter(b => b.readerId === reader.id);
      const totalFine = readerOverdue.reduce((sum, borrowing) => {
        const due = new Date(borrowing.dueDate);
        const today = new Date();
        const diffTime = today - due;
        const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
        return sum + (diffDays > 0 ? diffDays * 2 : 0);
      }, 0);

      return {
        ...reader,
        overdueCount: readerOverdue.length,
        totalFine,
        overdueBooks: readerOverdue.map(borrowing => {
          const book = books.find(b => b.id === borrowing.bookId);
          return {
            ...borrowing,
            bookTitle: book?.title,
            bookCallNumber: book?.callNumber
          };
        })
      };
    }).filter(reader => reader.overdueCount > 0)
      .sort((a, b) => b.overdueCount - a.overdueCount);

    return {
      totalOverdue: overdueBorrowings.length,
      totalFine: overdueBorrowings.reduce((sum, borrowing) => {
        const due = new Date(borrowing.dueDate);
        const today = new Date();
        const diffTime = today - due;
        const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
        return sum + (diffDays > 0 ? diffDays * 2 : 0);
      }, 0),
      overdueByReader
    };
  };

  const generateCollectionReport = () => {
    const booksBySubject = books.reduce((acc, book) => {
      const subject = book.subject || '未分類';
      if (!acc[subject]) {
        acc[subject] = [];
      }
      acc[subject].push(book);
      return acc;
    }, {});

    const subjectStats = Object.entries(booksBySubject).map(([subject, books]) => ({
      subject,
      count: books.length,
      available: books.filter(b => b.status === 'available').length,
      borrowed: books.filter(b => b.status === 'borrowed').length,
      percentage: ((books.filter(b => b.status === 'available').length / books.length) * 100).toFixed(1)
    })).sort((a, b) => b.count - a.count);

    const booksByLanguage = books.reduce((acc, book) => {
      const language = book.language || '未知';
      acc[language] = (acc[language] || 0) + 1;
      return acc;
    }, {});

    const booksByFormat = books.reduce((acc, book) => {
      const format = book.format || '未知';
      acc[format] = (acc[format] || 0) + 1;
      return acc;
    }, {});

    return {
      totalBooks: books.length,
      availableBooks: books.filter(b => b.status === 'available').length,
      borrowedBooks: books.filter(b => b.status === 'borrowed').length,
      subjectStats,
      booksByLanguage,
      booksByFormat
    };
  };

  const handleExport = (reportType) => {
    // In a real app, this would generate and download the report
    alert(`正在匯出 ${reportType} 報告 (${exportFormat.toUpperCase()} 格式)`);
  };

  const renderCirculationReport = () => {
    const report = generateCirculationReport();
    
    return (
      <div className="circulation-report">
        <div className="report-header">
          <h3>流通報告 - {report.period}</h3>
          <button 
            className="export-button"
            onClick={() => handleExport('circulation')}
          >
            匯出報告
          </button>
        </div>

        <div className="report-summary">
          <div className="summary-card">
            <div className="summary-value">{report.totalBorrowings}</div>
            <div className="summary-label">總借閱次數</div>
          </div>
          <div className="summary-card">
            <div className="summary-value">{report.totalReturns}</div>
            <div className="summary-label">總歸還次數</div>
          </div>
          <div className="summary-card">
            <div className="summary-value">{report.totalRenewals}</div>
            <div className="summary-label">總續借次數</div>
          </div>
          <div className="summary-card">
            <div className="summary-value">{report.totalOverdue}</div>
            <div className="summary-label">逾期次數</div>
          </div>
        </div>

        <div className="report-sections">
          <div className="report-section">
            <h4>熱門圖書 (Top 10)</h4>
            <div className="top-list">
              {report.topBooks.map((book, index) => (
                <div key={book.id} className="top-item">
                  <span className="rank">{index + 1}</span>
                  <span className="title">{book.title}</span>
                  <span className="count">{book.borrowCount} 次</span>
                </div>
              ))}
            </div>
          </div>

          <div className="report-section">
            <h4>活躍讀者 (Top 10)</h4>
            <div className="top-list">
              {report.topReaders.map((reader, index) => (
                <div key={reader.id} className="top-item">
                  <span className="rank">{index + 1}</span>
                  <span className="name">{reader.name} ({reader.studentId})</span>
                  <span className="count">{reader.borrowCount} 次</span>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    );
  };

  const renderOverdueReport = () => {
    const report = generateOverdueReport();
    
    return (
      <div className="overdue-report">
        <div className="report-header">
          <h3>逾期報告</h3>
          <button 
            className="export-button"
            onClick={() => handleExport('overdue')}
          >
            匯出報告
          </button>
        </div>

        <div className="report-summary">
          <div className="summary-card warning">
            <div className="summary-value">{report.totalOverdue}</div>
            <div className="summary-label">逾期圖書總數</div>
          </div>
          <div className="summary-card danger">
            <div className="summary-value">${report.totalFine}</div>
            <div className="summary-label">總罰款金額</div>
          </div>
        </div>

        <div className="overdue-readers">
          <h4>逾期讀者清單</h4>
          {report.overdueByReader.map(reader => (
            <div key={reader.id} className="overdue-reader">
              <div className="reader-header">
                <h5>{reader.name} ({reader.studentId}) - {reader.class}</h5>
                <span className="overdue-count">{reader.overdueCount} 本逾期</span>
                <span className="total-fine">罰款: ${reader.totalFine}</span>
              </div>
              <div className="overdue-books">
                {reader.overdueBooks.map(borrowing => (
                  <div key={borrowing.id} className="overdue-book">
                    <span className="book-title">{borrowing.bookTitle}</span>
                    <span className="call-number">{borrowing.bookCallNumber}</span>
                    <span className="due-date">到期: {formatDate(borrowing.dueDate)}</span>
                  </div>
                ))}
              </div>
            </div>
          ))}
        </div>
      </div>
    );
  };

  const renderCollectionReport = () => {
    const report = generateCollectionReport();
    
    return (
      <div className="collection-report">
        <div className="report-header">
          <h3>館藏報告</h3>
          <button 
            className="export-button"
            onClick={() => handleExport('collection')}
          >
            匯出報告
          </button>
        </div>

        <div className="report-summary">
          <div className="summary-card">
            <div className="summary-value">{report.totalBooks}</div>
            <div className="summary-label">總藏書量</div>
          </div>
          <div className="summary-card">
            <div className="summary-value">{report.availableBooks}</div>
            <div className="summary-label">可借圖書</div>
          </div>
          <div className="summary-card">
            <div className="summary-value">{report.borrowedBooks}</div>
            <div className="summary-label">已借出</div>
          </div>
          <div className="summary-card">
            <div className="summary-value">
              {((report.availableBooks / report.totalBooks) * 100).toFixed(1)}%
            </div>
            <div className="summary-label">可用率</div>
          </div>
        </div>

        <div className="report-sections">
          <div className="report-section">
            <h4>按主題分類統計</h4>
            <div className="subject-stats">
              {report.subjectStats.map(stat => (
                <div key={stat.subject} className="subject-stat">
                  <div className="subject-header">
                    <span className="subject-name">{stat.subject}</span>
                    <span className="subject-count">{stat.count} 本</span>
                  </div>
                  <div className="subject-bar">
                    <div 
                      className="bar-fill" 
                      style={{ width: `${stat.percentage}%` }}
                    ></div>
                  </div>
                  <div className="subject-details">
                    <span>可借: {stat.available}</span>
                    <span>已借: {stat.borrowed}</span>
                    <span>可用率: {stat.percentage}%</span>
                  </div>
                </div>
              ))}
            </div>
          </div>

          <div className="report-section">
            <h4>按語言分類</h4>
            <div className="language-stats">
              {Object.entries(report.booksByLanguage).map(([language, count]) => (
                <div key={language} className="language-stat">
                  <span className="language-name">{language}</span>
                  <span className="language-count">{count} 本</span>
                </div>
              ))}
            </div>
          </div>

          <div className="report-section">
            <h4>按格式分類</h4>
            <div className="format-stats">
              {Object.entries(report.booksByFormat).map(([format, count]) => (
                <div key={format} className="format-stat">
                  <span className="format-name">{format}</span>
                  <span className="format-count">{count} 本</span>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    );
  };

  return (
    <div className="library-reports">
      <div className="reports-header">
        <button className="back-button" onClick={onBack}>
          ← 返回目錄
        </button>
        <h2>圖書館報表系統</h2>
      </div>

      <div className="reports-controls">
        <div className="report-type-selector">
          <button
            className={`report-type-btn ${selectedReport === 'circulation' ? 'active' : ''}`}
            onClick={() => setSelectedReport('circulation')}
          >
            流通報告
          </button>
          <button
            className={`report-type-btn ${selectedReport === 'overdue' ? 'active' : ''}`}
            onClick={() => setSelectedReport('overdue')}
          >
            逾期報告
          </button>
          <button
            className={`report-type-btn ${selectedReport === 'collection' ? 'active' : ''}`}
            onClick={() => setSelectedReport('collection')}
          >
            館藏報告
          </button>
        </div>

        <div className="report-options">
          <select
            value={dateRange}
            onChange={(e) => setDateRange(e.target.value)}
            className="date-range-select"
          >
            <option value="week">最近一週</option>
            <option value="month">最近一個月</option>
            <option value="quarter">最近一季</option>
            <option value="year">最近一年</option>
          </select>

          <select
            value={exportFormat}
            onChange={(e) => setExportFormat(e.target.value)}
            className="export-format-select"
          >
            <option value="pdf">PDF</option>
            <option value="excel">Excel</option>
            <option value="csv">CSV</option>
          </select>
        </div>
      </div>

      <div className="reports-content">
        {selectedReport === 'circulation' && renderCirculationReport()}
        {selectedReport === 'overdue' && renderOverdueReport()}
        {selectedReport === 'collection' && renderCollectionReport()}
      </div>
    </div>
  );
};

export default LibraryReports;
